/**
 * What needs attention, derived from the fleet, plus the hook the bell badges from.
 *
 * Split out of `NotificationsPanel.tsx` for the mechanical reason the rest of this repo
 * already splits on: a `.tsx` exporting a non-component loses Fast Refresh for the module and
 * the lint runs at `--max-warnings 0`. Same precedent as `console-api.ts` and `session-api.ts`.
 *
 * There is no notification store, no unread state and no server-side feed, and none of this
 * pretends otherwise: every row is computed from the same deployment records the Fleet
 * Overview board renders, using the same helpers, so the bell and the board can never
 * disagree. When a real feed exists this is where it lands.
 */
import { useEffect, useState } from 'react'
import { useOptionalFleetApi, type DeploymentRecord } from '../../api/index.ts'
import { credentialWarning, driftReported, formatAge, healthOf, snapshotLag } from '../../screens/fleet/model/health.ts'
import { NOT_REPORTED } from '../../screens/fleet/model/heartbeat.ts'

export type Severity = 'critical' | 'warning'

export interface Item {
  id: string
  severity: Severity
  title: string
  detail: string
  href: string
}

/**
 * Attention items for one deployment, worst first.
 *
 * A deployment that is unreachable AND drifting produces two rows on purpose. They are two
 * different pieces of work for two different people, and merging them into "3 problems" is
 * how a count becomes something nobody can act on.
 */
export function itemsFor(d: DeploymentRecord, now: Date): Item[] {
  const out: Item[] = []
  const href = `/fleet/deployments/${encodeURIComponent(d.deploymentId)}`
  const who = `${d.clientName} · ${d.deploymentName}`
  const verdict = healthOf(d, now)

  if (verdict.state === 'unreachable') {
    out.push({
      id: `${d.deploymentId}:unreachable`,
      severity: 'critical',
      title: `${who} is unreachable`,
      // Silence is the primary failure signal in a push architecture (CONSOLES.md §2.1), so
      // this says how long rather than just that it happened.
      detail: `Last seen ${formatAge(verdict.ageSeconds)}.`,
      href,
    })
  } else if (verdict.state === 'degraded') {
    out.push({
      id: `${d.deploymentId}:degraded`,
      severity: 'warning',
      title: `${who} is degraded`,
      detail: `Last seen ${formatAge(verdict.ageSeconds)}.`,
      href,
    })
  } else if (verdict.state === 'never-enrolled') {
    // WITHOUT THIS BRANCH A NEVER-ENROLLED DEPLOYMENT RAISED NOTHING AT ALL. Not unreachable
    // (it is a different state), not degraded, and no drift row either because absent drift
    // scored 0 below. So the one deployment we had heard nothing whatever from was the only
    // one that produced perfect quiet - silence reading as health, in the model nobody looks
    // at directly, which is where it costs the most.
    out.push({
      id: `${d.deploymentId}:never-enrolled`,
      severity: 'warning',
      title: `${who} has never called home`,
      // Names what it would take, rather than only that it is missing (NthAI.md).
      detail: 'Enrolled, and no heartbeat has ever arrived. Check the relay started and that its egress is open.',
      href,
    })
  }

  // `driftCount` is optional on the message: absent means the deployment did not report one,
  // which is not the same as zero and must not render as "no drift".
  //
  // That comment sat directly above `?? 0`, which is the exact thing it forbids. Stating a
  // rule in a comment does not enforce it: `driftReported` returns a union, so the absent
  // case cannot be skipped without the type complaining.
  const drift = driftReported(d)
  if (drift !== NOT_REPORTED && drift > 0) {
    out.push({
      id: `${d.deploymentId}:drift`,
      severity: 'warning',
      title: `${who} has drifted`,
      detail: `${drift} definition${drift === 1 ? '' : 's'} differ from the library.`,
      href: `${href}/snapshot`,
    })
  }

  // Ahead is a bug, not a state (CONSOLES.md §2.1), so it is critical rather than a note.
  if (snapshotLag(d) === 'ahead') {
    out.push({
      id: `${d.deploymentId}:ahead`,
      severity: 'critical',
      title: `${who} is ahead of the library`,
      detail: 'A deployment holding a snapshot we never published is a bug, not a state.',
      href: `${href}/snapshot`,
    })
  }

  const credential = credentialWarning(d.credentialExpiresAt, now)
  if (credential) {
    out.push({
      id: `${d.deploymentId}:credential`,
      severity: 'warning',
      title: `${who} credential expiring`,
      detail: credential,
      href: `${href}`,
    })
  }

  return out
}

export const RANK: Record<Severity, number> = { critical: 0, warning: 1 }


/** How many items the bell should badge. Null means "we do not know", which shows no badge. */
export function useAttentionCount(): number | null {
  const api = useOptionalFleetApi()
  const [count, setCount] = useState<number | null>(null)

  useEffect(() => {
    if (!api) return
    let live = true
    void (async () => {
      try {
        const deployments = await api.listDeployments()
        if (!live) return
        const now = new Date()
        setCount(deployments.flatMap((d) => itemsFor(d, now)).length)
      } catch {
        // A badge is not the place to report a failure: the panel says so in words when it is
        // opened. A badge that rendered 0 on a failed read would be a lie in one character.
        if (live) setCount(null)
      }
    })()
    return () => {
      live = false
    }
  }, [api])

  return count
}
